// いつでも相談前処理のデータの取得、及び、まだ前処理が終わっていなかった場合のポーリングを管理するcomposable
// app/javascript/vue/components/AnytimeConsultation.vue から呼び出される

// 利用方法
// 初期化：created などのタイミングで、initializeMonitor を呼び出すこと。事前にanytimeConsultationのデータの取得が完了している必要があることに注意
// 例：
// created() {
//   this.$store.dispatch("findAnytimeConsultation", {
//     id: this.$route.params.id
//   }).then(() => {
//     this.consultationPreprocessingHandlers = createConsultationPreprocessingRequest({
//       store: this.$store,
//       getConsultation: () => this.consultation,
//     });  
//     this.consultationPreprocessingHandlers.initializeMonitor();
//   });
// }

// 別のページに離れるときは stopPolling を呼び出してポーリングを停止すること
// 例：
// beforeUnmount() {
//   this.handleBeforeUnload();
//   this.consultationPreprocessingHandlers.stopPolling();
//   window.removeEventListener("beforeunload", this.handleBeforeUnload); // イベントリスナーを削除
// },

// resetRequest が clearAnytimeConsultationObjects 系の actionsと同じ位置づけで、ポーリングの停止とストアの中身の初期化の両方を行う
// clearData: function () {
//   this.consultationPreprocessingHandlers.resetRequest();
// },

import * as mutationTypes from "@/vue/stores/mutation-types";

const POLLING_INTERVAL_MS = 10_000;
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;

type ConsultationPreprocessingStatus = "idle" | "polling" | "fetching" | "completed" | "failed";

type ConsultationPreprocessingRequest = {
  isComplete?: boolean;
};

type Consultation = {
  id?: number;
  createdAt?: string;
};

type StoreLike = {
  dispatch(
    action: "fetchConsultationPreprocessingRequest",
    payload: { consultationId: number }
  ): Promise<ConsultationPreprocessingRequest | null | undefined>;
  dispatch(action: "resetConsultationPreprocessingRequest"): Promise<unknown> | unknown;
  commit: (mutationType: string, payload: ConsultationPreprocessingStatus) => void;
  getters: {
    consultationPreprocessingStatus?: ConsultationPreprocessingStatus;
  };
};

type CreateConsultationPreprocessingRequestParams = {
  store: StoreLike;
  getConsultation: () => Consultation | null | undefined;
};

// 相談の作成時刻をミリ秒に変換する
const _parseCreatedAt = (consultationCreatedAt?: string): number | null => {
  if (!consultationCreatedAt) return null;

  const createdMs = Date.parse(consultationCreatedAt);
  if (Number.isNaN(createdMs)) return null;

  return createdMs;
};

// いつでも相談の投稿時刻から 5分(POLLING_TIMEOUT_MS) 経過したら、疑問の抽出は失敗したと見なす
const _isExpired = (createdMs: number) => Date.now() - createdMs >= POLLING_TIMEOUT_MS;

// 相談前処理の監視・ポーリングを管理する
export const createConsultationPreprocessingRequest = ({
  store,
  getConsultation,
}: CreateConsultationPreprocessingRequestParams) => {
  let pollingTimerId: number | null = null;
  let createdAtMs: number | null = null;

  // ConsultationPreprocessingRequestを取得を試み、疑問の抽出ステータスに応じて処理を分岐する。
  // 1. 取得成功 -> 処理を完了する。
  // 2. 現時点で疑問の抽出が未完了 => 10s(POLLING_INTERVAL_MS)間隔のPollingを開始する。
  const initializeMonitor = async (): Promise<void> => {
    const consultation = getConsultation();
    const consultationId = consultation?.id;
    const consultationCreatedAt = consultation?.createdAt;
    if (!consultationId || !consultationCreatedAt) {
      return;
    }

    const result = await store.dispatch("fetchConsultationPreprocessingRequest", {
      consultationId,
    });
    if (!result) return;
    if (result.isComplete) return;

    _startPolling({ consultationId, consultationCreatedAt });
  };

  // ポーリング（10s間隔で_tickを実行するようにsetIntervalにセット）を開始。
  const _startPolling = ({
    consultationId,
    consultationCreatedAt,
  }: {
    consultationId?: number;
    consultationCreatedAt?: string;
  }): void => {
    const createdMs = _parseCreatedAt(consultationCreatedAt);
    if (!consultationId || !createdMs) return;

    if (_isExpired(createdMs)) {
      store.commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
      return;
    }

    createdAtMs = createdMs;

    stopPolling("polling");

    pollingTimerId = window.setInterval(() => {
      void _tick(consultationId);
    }, POLLING_INTERVAL_MS);
  };

  // ポーリングを停止し、指定ステータスに更新する
  const stopPolling = (nextStatus: ConsultationPreprocessingStatus = "idle"): void => {
    if (pollingTimerId !== null) {
      clearInterval(pollingTimerId);
    }
    pollingTimerId = null;

    store.commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, nextStatus);
  };

  // ポーリング処理(1回分)を実行する
  // 状態遷移ルール：
  // ・期限切れ（いつでも相談の投稿時刻から 5分(POLLING_TIMEOUT_MS) 経過）なら、failedステータスに以降
  // ・現在通信中（fetchingステータス）なら、何もしない
  // ・再取得を試み、isComplete == true のデータが取得できたら、completedステータスに移行してポーリングを停止
  // ・再取得を試みたものの、isCompleteのデータが取得できなかったら、ステータスを変更せずpollingを継続する
  const _tick = async (consultationId: number): Promise<void> => {
    if (!createdAtMs || _isExpired(createdAtMs)) {
      stopPolling("failed");
      return;
    }

    if (store.getters.consultationPreprocessingStatus === "fetching") {
      return;
    }

    const request = await store.dispatch("fetchConsultationPreprocessingRequest", {
      consultationId,
    });

    if (request?.isComplete) {
      stopPolling("completed");
    }
  };

  // ステータスとストアの状態を初期化する
  const resetRequest = (): void => {
    stopPolling("idle");
    void store.dispatch("resetConsultationPreprocessingRequest");
  };

  return {
    initializeMonitor,
    stopPolling,
    resetRequest,
    // 以下、spec用にexportする
    _startPolling,
    _tick,
  };
};